import { FC, useEffect, useRef, useState } from "react";
import { format as formatTimeAgo } from "timeago.js";
import { BACKEND_AI_AGENT_URL, supportedChainsMap } from "../../config";
import { RequestAbortedError } from "../../errors";
import {
  abbreviateNumber,
  tokenAmountDividedByDecimals,
} from "../../utils/utils";
import { ArrowRightIcon } from "./ArrowRightIcon";
import { OpenInNewTabIcon } from "./OpenInNewTabIcon";

// Example: "en-US"
// undefined - use user's locale
const locale = undefined;

export type SignificantTransfer = {
  fromAddress: string;
  toAddress: string;
  valueUSD: number;
  timestampMs: number;
  chainId: string;
  txHash: string;
  symbol: string;
  decimals: number;
  amount: string;
  tokenAddress: string;
};

// const TradeSide: FC<{
//   amount: string;
//   amountUSD: number;
//   symbol: string;
//   decimals: number;
//   tokenAddress: string;
//   walletAddress: string;
//   chainId: string;
//   className?: string;
// }> = ({
//   amount,
//   amountUSD,
//   symbol,
//   decimals,
//   tokenAddress,
//   walletAddress,
//   chainId,
//   className,
// }) => {
//   return (
//     <div className={`flex flex-col gap-1 ${className || ""}`}>
//       <div className="flex items-center gap-1">
//         <img
//           src={`https://xswap.link/assets/tokens/${chainId}_${tokenAddress}_${symbol}.webp`}
//           alt={`${symbol} logo`}
//           className="w-[20px] h-[20px] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px] rounded-full"
//         />
//         <div className="text-white font-medium text-xs whitespace-nowrap">
//           {`${tokenAmountDividedByDecimals({ amount, decimals })} ${symbol}`}
//         </div>
//       </div>
//       <div className="text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
//         {`${amountUSD.toLocaleString(locale)} USD`}
//       </div>
//       <div className="text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
//         {walletAddress.slice(0, 4)}****
//       </div>
//     </div>
//   );
// };

const Transaction: FC<{
  amount: string;
  amountUSD: number;
  symbol: string;
  decimals: number;
  tokenAddress: string;
  fromAddress: string;
  toAddress: string;
  chainId: string;
  className?: string;
}> = ({
  amount,
  amountUSD,
  symbol,
  decimals,
  tokenAddress,
  fromAddress,
  toAddress,
  chainId,
  className,
}) => {
  return (
    <div className={`flex items-center gap-4 ${className || ""}`}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <img
            src={`https://xswap.link/assets/tokens/${chainId}_${tokenAddress}_${symbol}.webp`}
            alt={`${symbol} logo`}
            className="w-[20px] h-[20px] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px] rounded-full"
          />
          <div className="text-white font-medium text-xs whitespace-nowrap">
            {`${abbreviateNumber(tokenAmountDividedByDecimals({ amount, decimals }))} ${symbol}`}
          </div>
        </div>

        <div className="text-[10px] lg:text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
          {`${amountUSD.toLocaleString(locale)} USD`}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <div className="text-[10px] lg:text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
          {fromAddress.slice(0, 4)}****
        </div>
        <ArrowRightIcon className="w-6 min-w-6 max-w-6 h-6 min-h-6 max-h-6" />
        <div className="text-[10px] lg:text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
          {toAddress.slice(0, 4)}****
        </div>
      </div>
    </div>
  );
};

export const SignificantTokenTransfers: FC<{
  projectId?: string;
  className?: string;
}> = ({ projectId, className }) => {
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState<SignificantTransfer[]>([]);
  const fetchSignificantTransfersRef = useRef(new AbortController());

  const DATE_COLUMN_CLASS = "w-[60px] lg:w-[75px]";
  const TRANSACTION_COLUMN_CLASS = "w-full lg:w-[245px]";
  const OPEN_LINK_COLUMN_CLASS = "w-[60px]";

  useEffect(() => {
    (async () => {
      fetchSignificantTransfersRef.current.abort(new RequestAbortedError());
      fetchSignificantTransfersRef.current = new AbortController();
      setLoading(true);

      let response: Awaited<ReturnType<typeof fetch>>;
      try {
        response = await fetch(
          `${BACKEND_AI_AGENT_URL}/significant-transfers${projectId ? `/${projectId}` : ""}`,
          {
            signal: fetchSignificantTransfersRef.current.signal,
          },
        );
      } catch (err) {
        if (err instanceof RequestAbortedError) {
          return;
        }

        setLoading(false);
        throw err;
      }

      const data = (await response.json()) as {
        transfers: SignificantTransfer[];
      };

      setTransfers(data.transfers.slice(0, 5));
      setLoading(false);
    })();
  }, [projectId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!loading && !transfers.length) {
    return <div>No significant transfers</div>;
  }

  return (
    <div
      className={`w-full relative flex flex-col gap-[10px] ${className || ""}`}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="watermark"></div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-white font-bold text-base">
          Significant token transfers
        </div>
        <a
          href={
            projectId
              ? `/report/significant-token-transfers/${projectId}`
              : "/significant-token-transfers"
          }
          className="text-[#007DFF] font-medium text-base"
        >{`View all >`}</a>
      </div>
      <div className="flex items-center justify-between text-sm font-normal text-[rgba(255,255,255,0.5)] gap-4">
        <div className={`${DATE_COLUMN_CLASS}`}>Date</div>
        <div
          className={`${TRANSACTION_COLUMN_CLASS} flex items-center justify-center lg:justify-start`}
        >
          Transaction
        </div>
        <div
          className={`${OPEN_LINK_COLUMN_CLASS} text-right ml-auto hidden min-[1300px]:block whitespace-nowrap`}
        >
          Open link
        </div>
      </div>
      <div className="flex flex-col">
        {transfers.map((transfer) => (
          <div
            key={`${transfer.txHash}`}
            className="flex items-center gap-2 flex-1  border-solid border-b border-[rgba(255,255,255,0.05)] last:border-none pb-2 mb-2"
          >
            <div className={`${DATE_COLUMN_CLASS} text-[10px] sm:text-xs`}>
              {formatTimeAgo(transfer.timestampMs, locale)}
            </div>
            <div
              className={`${TRANSACTION_COLUMN_CLASS} flex items-center justify-center gap-1`}
            >
              <Transaction
                symbol={transfer.symbol}
                decimals={transfer.decimals}
                tokenAddress={transfer.tokenAddress}
                fromAddress={transfer.fromAddress}
                toAddress={transfer.toAddress}
                amountUSD={transfer.valueUSD}
                amount={transfer.amount}
                chainId={transfer.chainId}
              />
              {/* @TODO when aggregators start scraping transactions with different tokenIn and tokenOut, we should uncomment this */}
              {/* <TradeSide
                symbol={transfer.symbol}
                decimals={transfer.decimals}
                tokenAddress={transfer.tokenAddress}
                walletAddress={transfer.fromAddress}
                amountUSD={transfer.valueUSD}
                amount={transfer.amount}
                chainId={transfer.chainId}
              />
              <ArrowRightIcon className="w-6 min-w-6 max-w-6 h-6 min-h-6 max-h-6" />
              <TradeSide
                symbol={transfer.symbol}
                decimals={transfer.decimals}
                tokenAddress={transfer.tokenAddress}
                walletAddress={transfer.toAddress}
                amountUSD={transfer.valueUSD}
                amount={transfer.amount}
                chainId={transfer.chainId}
              /> */}
            </div>
            <div
              className={`${OPEN_LINK_COLUMN_CLASS} flex ml-auto hidden min-[1300px]:block text-right`}
            >
              <a
                href={supportedChainsMap.evm[
                  transfer.chainId
                ].getTransactionExplorerUrl(transfer.txHash)}
                target="_blank"
                rel="noreferrer"
                aria-label="Go to blockchain scan"
                className="ml-auto"
              >
                <OpenInNewTabIcon />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
